/**
 * Graba un dictado completo en un solo archivo, sin transcribirlo.
 *
 * Es el otro modo de dictar: el especialista habla de corrido, el audio queda
 * adjunto al estudio y la transcripcion es un acto posterior, del motor o de
 * una persona. Por eso aqui no hay trozos ni envios: una grabacion, un
 * archivo.
 *
 * @module application/dictation/useGrabacionDeDictado
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { pickRecordingMimeType } from './dictation.service'

export interface UseGrabacionDeDictado {
  grabando: boolean
  segundos: number
  disponible: boolean
  iniciar: () => Promise<void>
  /** Cierra la grabacion y entrega el archivo. */
  detener: () => Promise<Blob | null>
  cancelar: () => void
}

export function useGrabacionDeDictado(
  onError?: (mensaje: string) => void
): UseGrabacionDeDictado {
  const [grabando, setGrabando] = useState(false)
  const [segundos, setSegundos] = useState(0)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const trozosRef = useRef<Blob[]>([])
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const soltar = useCallback(() => {
    streamRef.current?.getTracks().forEach((pista) => pista.stop())
    streamRef.current = null
    recorderRef.current = null
    setGrabando(false)
  }, [])

  useEffect(() => {
    if (!grabando) return
    const reloj = setInterval(() => setSegundos((s) => s + 1), 1000)
    return () => clearInterval(reloj)
  }, [grabando])

  useEffect(() => soltar, [soltar])

  const iniciar = useCallback(async () => {
    if (recorderRef.current) return

    const mimeType = pickRecordingMimeType()
    if (!mimeType) {
      onErrorRef.current?.('Este navegador no puede grabar audio')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const recorder = new MediaRecorder(stream, { mimeType })
      trozosRef.current = []
      recorder.ondataavailable = (evento) => {
        if (evento.data.size > 0) trozosRef.current.push(evento.data)
      }
      recorder.onerror = () => {
        onErrorRef.current?.('Falló la grabación de audio')
        soltar()
      }

      recorderRef.current = recorder
      recorder.start()
      setSegundos(0)
      setGrabando(true)
    } catch {
      onErrorRef.current?.(
        'No se pudo acceder al micrófono. Revise el permiso del navegador y que haya un micrófono conectado.'
      )
    }
  }, [soltar])

  const detener = useCallback(async () => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state !== 'recording') {
      soltar()
      return null
    }

    const archivo = await new Promise<Blob>((resolver) => {
      recorder.onstop = () =>
        resolver(new Blob(trozosRef.current, { type: recorder.mimeType }))
      recorder.stop()
    })

    soltar()
    return archivo.size > 0 ? archivo : null
  }, [soltar])

  const cancelar = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state === 'recording') {
      recorder.onstop = null
      recorder.stop()
    }
    trozosRef.current = []
    soltar()
  }, [soltar])

  return {
    grabando,
    segundos,
    disponible: typeof MediaRecorder !== 'undefined',
    iniciar,
    detener,
    cancelar,
  }
}
